import { useUnit } from 'effector-react'
import { useStyles } from './styles'
import {
  $friendsList,
  $userLanguage,
  $usersByGamedId,
  $usersByGamedIdPagination,
  $userToken,
  clearUsersByGamedId,
  Friend,
  getFriendsListFx,
  getUsersByGamedIdFx,
  UserByGamedId,
} from '@/entities/user'
import { lang } from '@/shared/constants/lang'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FriendsItem, SearchedUserItem } from '@/entities/user/ui/referrallItem'
import AppLoading from '@/features/loading'
import { Icon } from '@/shared/ui/icon'
import { debounce } from '@/shared/util/debounce'
import { Skeleton } from '@mui/material'

type ContentToRenderProps = {
  searchValue: string
  noResults: boolean
  isFetching: boolean
  friendsList: Friend[]
  searchedList: UserByGamedId[] | null
}

export const Friends = () => {
  const { classes } = useStyles()
  const userLang = useUnit($userLanguage)
  const texts = lang[userLang].pages.friends
  const userToken = useUnit($userToken)
  const friendsList = useUnit($friendsList)
  const usersByGamedId = useUnit($usersByGamedId)
  const [searchValue, setSearchValue] = useState('')
  const [isFetching, setIsFetching] = useState(false)

  useEffect(() => {
    if (userToken) {
      getFriendsListFx({ size: 1000, page: 1, token: userToken })
    }
  }, [userToken])

  const [lastFailedSearch, setLastFailedSearch] = useState<string | null>(null)

  const searchUsersDebounced = useMemo(
    () =>
      debounce(async (value: string) => {
        if (!value || !userToken) {
          setIsFetching(false)
          return
        }

        if (lastFailedSearch && value.startsWith(lastFailedSearch)) {
          setIsFetching(false)
          clearUsersByGamedId()
          return
        }

        clearUsersByGamedId()
        const result = await getUsersByGamedIdFx({ gamed_id: value, page: 1, size: 10, token: userToken })

        if (result.pagination.total === 0) {
          setLastFailedSearch(value)
        } else {
          setLastFailedSearch(null)
        }

        setIsFetching(false)
      }, 500),
    [userToken, lastFailedSearch]
  )

  const subHeading = searchValue.length > 0 ? texts.results : texts.myFriends
  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setIsFetching(true)

    setSearchValue(value)
    await searchUsersDebounced(value)
    setIsFetching(false)
  }

  if (!friendsList) return <AppLoading />
  return (
    <div className={classes.root}>
      <span className={classes.heading}>{texts.friends}</span>
      <div className={classes.inputWrapper}>
        <Icon view="addFriend" width={28} />
        <input className={classes.input} placeholder={texts.enterId} value={searchValue} onChange={handleChange} />
      </div>

      <span className={classes.subHeading}>{subHeading}:</span>
      <ContentToRender
        searchValue={searchValue}
        noResults={searchValue.length > 0 && usersByGamedId?.length === 0}
        isFetching={isFetching}
        friendsList={friendsList}
        searchedList={usersByGamedId}
      />
    </div>
  )
}

const ContentToRender = (props: ContentToRenderProps) => {
  const userLang = useUnit($userLanguage)
  const texts = lang[userLang].pages.friends
  const usersByGamedIdPagination = useUnit($usersByGamedIdPagination)
  const { classes } = useStyles()
  const userToken = useUnit($userToken)
  const { searchValue, noResults, isFetching, friendsList, searchedList } = props
  const triggerRef = useRef<HTMLDivElement | null>(null)
  const [isFetchingMore, setIsFetchingMore] = useState(false)

  const isSearching = searchValue.length > 0

  const handleLoadMore = useCallback(async () => {
    if (!userToken || !usersByGamedIdPagination || isFetchingMore) return

    const { page, pages } = usersByGamedIdPagination
    if (page >= pages) return

    setIsFetchingMore(true)
    await getUsersByGamedIdFx({
      gamed_id: searchValue,
      page: page + 1,
      size: 10,
      token: userToken,
    })
    setIsFetchingMore(false)
  }, [userToken, searchValue, usersByGamedIdPagination, isFetchingMore])

  useEffect(() => {
    const observerTarget = triggerRef.current
    if (!observerTarget || !searchValue || noResults) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          handleLoadMore()
        }
      },
      { rootMargin: '200px' }
    )

    observer.observe(observerTarget)

    return () => {
      if (observerTarget) observer.unobserve(observerTarget)
    }
  }, [triggerRef, searchValue, usersByGamedIdPagination, noResults, handleLoadMore])

  if (isSearching && (isFetching || searchedList === null)) {
    return (
      <div className={classes.list}>
        {Array.from({ length: 10 }).map(() => (
          <Skeleton variant="rounded" width={'100%'} height={68} style={{ borderRadius: '12px' }} />
        ))}
      </div>
    )
  }
  if (isSearching) {
    if (noResults) {
      return (
        <div className={classes.noResultsWrapper}>
          <span className={classes.noResults}>{texts.noResults}</span>
          <span className={classes.noResultsId}>{searchValue}</span>
        </div>
      )
    }

    return (
      <div className={classes.list}>
        {searchedList!.map((user, index) => {
          const isLast = index === searchedList!.length - 1
          return (
            <div key={user.gamed_id} ref={isLast ? triggerRef : null}>
              <SearchedUserItem {...user} />
            </div>
          )
        })}

        {isFetchingMore && (
          <>
            {Array.from({ length: 10 }).map((_, index) => (
              <Skeleton
                key={`loadmore-skeleton-${index}`}
                variant="rounded"
                height={68}
                style={{ borderRadius: '12px' }}
              />
            ))}
          </>
        )}
      </div>
    )
  }

  return (
    <div className={classes.list}>
      {friendsList.map((friend) => (
        <FriendsItem
          key={friend.friend_tid}
          username={friend.username}
          gamed_id={friend.gamed_id}
          photo_url={friend.avatar}
          level_premium={friend.premium_level}
          tid={friend.friend_tid}
        />
      ))}
    </div>
  )
}
